import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMAGES } from "@/data";

const reveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.15 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.7]);

  const scrollToEnquiry = () => document.querySelector("#enquiry")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero">
      <motion.div style={{ scale, y }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Nova Royale aerial landscape at golden hour" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-[#0e1416]" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0e1416]/90 via-transparent to-[#0e1416]/40" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 md:px-10 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-6 text-xs uppercase tracking-[0.45em] text-[var(--gold-bright)]"
          data-testid="hero-overline"
        >
          N Murthy Groups · Premium Gated Community Plots
        </motion.p>

        <h1 className="font-serif-display text-[var(--cream)]">
          {["Nova Royale", "where your", "future takes root."].map((line, i) => (
            <span key={i} className="hero-line-mask">
              <motion.span
                custom={i}
                variants={reveal}
                initial="hidden"
                animate="show"
                className={`block leading-[0.95] tracking-tight ${
                  i === 0
                    ? "text-5xl italic sm:text-7xl lg:text-8xl"
                    : "text-4xl sm:text-6xl lg:text-7xl"
                } ${i === 1 ? "text-[var(--cream-3)]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <button
            onClick={scrollToEnquiry}
            className="rounded-sm bg-[var(--gold)] px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform duration-300 hover:-translate-y-1 hover:bg-[var(--gold-bright)]"
            data-testid="hero-cta-btn"
          >
            Schedule a Site Visit
          </button>
          <p className="max-w-xs text-sm leading-relaxed text-[var(--cream-3)]">
            9 Acres 24 Guntas of thoughtfully planned land. BMRDA approved, ready for registration.
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-5 z-10 hidden text-[10px] uppercase tracking-[0.3em] text-[var(--cream-3)] md:block md:right-10"
      >
        Invest Today · Build Your Dream Tomorrow
      </motion.div>
    </section>
  );
}
